/**
 * Developer hint:
 * To see what physical space any @react-pdf element uses, add the `debug` prop to the component.
 */

import {
  Image,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFViewer,
} from '@react-pdf/renderer';
import { format, isSameDay, startOfDay } from 'date-fns';
import type { DerivedTeamRoster } from '../../hooks/types';
import { hyphenateLongWords, truncate } from '../../lib/utils';
import logo from '../../logo.png';
import type { Role, Orientation, Division, Roster, Shift } from './types';
import { stackedDivisions, teamRosterThisDay } from './util';

export const styles = StyleSheet.create({
  page: { flexDirection: 'column', alignItems: 'center' },
  horizontalStack: { flexDirection: 'row', margin: 1 },
  verticalStack: { flexDirection: 'column', margin: 1 },
  horizontalStackVerticallyCentred: {
    flexDirection: 'row',
    margin: 1,
    alignItems: 'center',
  },
  verticalStackHorizontallyCentred: {
    margin: 1,
    flexDirection: 'column',
    alignItems: 'center',
  },
  roleOuter: {
    margin: 1,
    borderRadius: 5,
    borderWidth: 3,
    borderStyle: 'solid',
  },
  roleBackground: { opacity: 0.2 },
  roleHeader: {
    padding: 4,
    opacity: 1,
    borderBottomWidth: 3,
    borderBottomStyle: 'solid',
  },
  roleShifts: { padding: 5, opacity: 1 },
  shift: { flexDirection: 'row', justifyContent: 'space-between', gap: 5 },
  teamDetails: { paddingLeft: 5 },
  image: { height: 80, margin: 5 },
  textSmall: { fontSize: 7, opacity: 1 },
  nightShift: {
    fontSize: 7,
    opacity: 1,
    fontFamily: 'Helvetica-Oblique',
    color: 'purple',
  },
  textMedium: { fontSize: 9, opacity: 1 },
  textLarge: { fontSize: 20, opacity: 1 },
  teamHeader: { fontSize: 14, opacity: 1, paddingLeft: 5 },
});

/**
 * Takes a day and a shift and determines how it should be rendered within a
 * role box in the roster.
 *
 * @param date - The day that the roster is being rendered for, in milliseconds.
 * @param dayShiftPeriod - The shift period for the day.
 * @returns - A function that takes a shift and returns the JSX element to render it.
 */
const ShiftDisplay: React.FC<{
  readonly date: number;
  readonly shift: Shift;
}> = ({ date, shift }): JSX.Element => {
  const midnight = startOfDay(date);
  const start = format(shift.startTime, 'HHmm');
  const end = format(shift.endTime, 'HHmm');

  // Shifts that overlap an adjacent day are rendered with the date in brackets.
  const startTimeIsYesterday = !isSameDay(shift.startTime, midnight);
  const endTimeIsTomorrow = !isSameDay(shift.endTime, midnight);
  const startTime = startTimeIsYesterday ? `(${start})` : start;
  const endTime = endTimeIsTomorrow ? `(${end})` : end;

  const isNight = startTimeIsYesterday || endTimeIsTomorrow;

  return (
    <View style={styles.shift}>
      {/* Night shifts are rendered in italicised purple */}
      <Text style={isNight ? styles.nightShift : styles.textSmall}>
        {shift.name}
        {isNight ? ' (N)' : ''}
      </Text>
      <Text style={isNight ? styles.nightShift : styles.textSmall}>
        {`${startTime}-${endTime}`}
      </Text>
    </View>
  );
};

/**
 * One role and its shifts.
 * A leader is highlighted.
 */
const TeamRole: React.FC<{
  readonly role: Role;
  readonly color: string;
  readonly date: number;
}> = ({ role, color, date }) => {
  return (
    <View style={[styles.roleOuter, { borderColor: color }]}>
      <View
        style={[
          styles.roleBackground,
          {
            backgroundColor: role.isLeader ? color : 'transparent',
            flexGrow: 1,
          },
        ]}
      >
        <View style={[styles.roleHeader, { borderBottomColor: color }]}>
          <Text style={styles.textMedium}>{role.title}</Text>
        </View>

        <View style={styles.roleShifts}>
          {role.shifts.map((shift, j) => (
            <ShiftDisplay key={j} shift={shift} date={date} />
          ))}
        </View>
      </View>
    </View>
  );
};

/**
 * A stack of Roles.
 */
const RoleStack: React.FC<{
  readonly roles: readonly Role[];
  readonly color: string;
  readonly orientation: Orientation;
  readonly date: number;
}> = ({ roles, color, orientation, date }) => (
  <View
    style={
      orientation === 'horizontal'
        ? styles.horizontalStack
        : styles.verticalStack
    }
  >
    {roles.map((role, i) => (
      <TeamRole role={role} color={color} key={i} date={date} />
    ))}
  </View>
);

/**
 * A horizontal collection of RoleStacks.
 */
const Divisions: React.FC<{
  readonly divisions: readonly Division[];
  /**
   * The orientation of the RoleStacks within the Divisions.
   */
  readonly orientation: Orientation;
  readonly date: number;
}> = ({ divisions, orientation, date }) => (
  <View style={styles.horizontalStack}>
    {stackedDivisions(divisions).map((stack, i) => (
      <View style={styles.verticalStackHorizontallyCentred} key={i}>
        <View style={styles.verticalStack}>
          {stack.map((division) => (
            <View
              style={[styles.verticalStack, { marginBottom: 10 }]}
              key={division.title}
            >
              <Text style={styles.teamHeader}>{division.title}</Text>
              <RoleStack
                roles={division.roles}
                color={division.colour}
                orientation={orientation}
                date={date}
              />
            </View>
          ))}
        </View>
      </View>
    ))}
  </View>
);

/*
 * The header of the page.
 */
const PageHeader: React.FC<{
  readonly roster: Roster;
  readonly pageTitle: string;
  readonly date: string;
}> = ({ roster, pageTitle, date }) => (
  <View style={styles.horizontalStack}>
    <Image src={logo} style={styles.image}></Image>

    <View style={styles.horizontalStackVerticallyCentred}>
      <View style={styles.verticalStack}>
        <Text style={[styles.textLarge, { maxWidth: '400px' }]}>
          {pageTitle}
        </Text>
        <Text style={[styles.textLarge, { maxWidth: '400px' }]}>{date}</Text>
        <View style={styles.teamDetails}>
          <Text style={styles.textMedium}>
            {`ION: ${roster.ion} (${roster.ionDescription})`}
          </Text>

          <Text style={styles.textMedium}>Details:</Text>
          {roster.details.map((detail, i) => (
            <Text style={styles.textSmall} key={i}>
              - {detail}
            </Text>
          ))}
        </View>
      </View>
    </View>
  </View>
);

/**
 * The roster as a PDF Document.
 */
const RosterDocument: React.FC<{
  readonly team: Roster;
  readonly date: number;
}> = ({ team, date }) => {
  // Break lines in the title - only affects long words.
  // React PDF has an automatic hyphenation at appropriate places in words,
  // but in long words without natural breaking points, the automatic hyphenation
  // is not applied.
  const fullTitle = hyphenateLongWords(team.name);

  // Limit the name length for the PDF file title
  const dateString = format(date, 'EEEE, do MMMM yyyy');
  const trimmedTitle = `${truncate(team.name, 20)} ${dateString}`;

  return (
    <Document title={`Roster - ${trimmedTitle}`} creator="PRAS" producer="PRAS">
      <Page size="A3" style={styles.page} orientation="landscape">
        {/* Page Header */}
        <PageHeader roster={team} pageTitle={fullTitle} date={dateString} />

        {/* Executives */}
        <Divisions
          divisions={team.topDivisions}
          orientation={'horizontal'}
          date={date}
        />

        {/* Team Divisions */}
        <Divisions
          divisions={team.bottomDivisions}
          orientation={'vertical'}
          date={date}
        />
      </Page>
    </Document>
  );
};

const DailyRoster: React.FC<{
  readonly team: DerivedTeamRoster;
  readonly date: number;
  readonly height?: string;
  readonly showToolbar?: boolean;
}> = ({ team, date, height = '100%' }) => {
  return (
    <PDFViewer style={{ width: '100%', height }}>
      <RosterDocument team={teamRosterThisDay(team, date)} date={date} />
    </PDFViewer>
  );
};

export default DailyRoster;
